import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import api from '../../lib/api'

export const Route = createFileRoute('/dashboard/skills-bank')({
  component: SkillsBankComponent,
})

interface Skill {
  id: number
  name: string
  category: string
  description?: string
}

interface SkillFormData {
  name: string
  category: string
  description?: string
}

const skillsApi = {
  getAll: async () => {
    const response = await api.get<{ skills: Skill[] }>('/skills')
    return response.data
  },
  create: async (data: SkillFormData) => {
    const response = await api.post<{ skill: Skill; message: string }>('/skills', { skill: data })
    return response.data
  },
  update: async (id: number, data: SkillFormData) => {
    const response = await api.put<{ skill: Skill; message: string }>(`/skills/${id}`, { skill: data })
    return response.data
  },
  delete: async (id: number) => {
    await api.delete(`/skills/${id}`)
  },
}

function SkillsBankComponent() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null)
  const queryClient = useQueryClient()

  // Get current user info
  const { data: currentUserData } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await fetch('/api/v1/current_user', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      return response.json()
    }
  })

  const currentUser = currentUserData?.user
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner'

  // Fetch all skills
  const { data, isLoading, error } = useQuery({
    queryKey: ['skillsBank'],
    queryFn: skillsApi.getAll,
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: skillsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillsBank'] })
      queryClient.invalidateQueries({ queryKey: ['allSkills'] })
      setIsModalOpen(false)
      setEditingSkill(null)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: SkillFormData }) =>
      skillsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillsBank'] })
      queryClient.invalidateQueries({ queryKey: ['allSkills'] })
      setIsModalOpen(false)
      setEditingSkill(null)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: skillsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillsBank'] })
      queryClient.invalidateQueries({ queryKey: ['allSkills'] })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      name: formData.get('name') as string,
      category: formData.get('category') as string,
      description: formData.get('description') as string,
    }

    if (editingSkill) {
      updateMutation.mutate({ id: editingSkill.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (skill: Skill) => {
    setEditingSkill(skill)
    setIsModalOpen(true)
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this skill from the bank? This will affect all users who have this skill.')) {
      deleteMutation.mutate(id)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingSkill(null)
  }

  // Group skills by category
  const skillsByCategory = data?.skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = []
    }
    acc[skill.category].push(skill)
    return acc
  }, {} as Record<string, Skill[]>) || {}

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Skills Bank</h1>
            <p className="text-base-content/70 mt-1">
              Manage the master list of skills available in the system
            </p>
          </div>
          {isAdmin && (
            <button
              className="btn btn-accent text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Add Skill
            </button>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center py-8">Loading skills...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-error">
            <span>Error: {error.message}</span>
          </div>
        )}

        {/* Skills by Category */}
        {data && data.skills && Object.keys(skillsByCategory).length > 0 && (
          <div className="space-y-6">
            {Object.entries(skillsByCategory).map(([category, skills]) => (
              <div key={category} className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title text-2xl mb-4">{category}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {skills.map((skill) => (
                      <div key={skill.id} className="border border-base-300 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg">{skill.name}</h3>
                            {skill.description && (
                              <p className="text-sm text-base-content/70 mt-1">{skill.description}</p>
                            )}
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2 ml-4">
                              <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => handleEdit(skill)}
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                className="btn btn-sm btn-ghost text-error"
                                onClick={() => handleDelete(skill.id)}
                                disabled={deleteMutation.isPending}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {data && data.skills && data.skills.length === 0 && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center text-base-content/50 py-8">
                No skills in the bank yet. Click "Add Skill" to create your first skill!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {editingSkill ? 'Edit Skill' : 'Add New Skill'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Skill Name *</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="input input-bordered"
                  required
                  defaultValue={editingSkill?.name}
                  placeholder="e.g. Swedish Massage"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Category *</span>
                </label>
                <input
                  type="text"
                  name="category"
                  className="input input-bordered"
                  required
                  defaultValue={editingSkill?.category}
                  placeholder="e.g. Massage Therapy"
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description</span>
                </label>
                <textarea
                  name="description"
                  className="textarea textarea-bordered h-24"
                  defaultValue={editingSkill?.description}
                  placeholder="Brief description of the skill"
                />
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  className="btn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-accent text-white"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingSkill
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCloseModal}>close</button>
          </form>
        </dialog>
      )}
    </>
  )
}
